import React, { useState, useEffect, ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import StatusCode from '../../../../../../util/StatusCode';
import Loading from '../../../../../misc/Loading';
import { GetDnpActivities, GetActivitiesBySchool, ClearActivitiesBySchool, GetMappedDnpActivities, SaveActivityMappings, GetDnpSchoolTypes, BeginSaveActivityMappings, SaveActivityMappingsState, AbortSaveActivityMappings, ResetSaveActivityMappings } from '../../../../../../store/DnpAction';
import UnmappedDnpActivities from './UnmappedDnpActivities';
import MappedDnpActivities from './MappedDnpActivities';
import DnpActivitiesFilter from './DnpActivitiesFilter';
import { useParams, useSearchParams } from 'react-router-dom'
import RequestParser from '../../../../../../util/RequestParser';
import DnpActivitiesFooter from './DnpActivitiesFooter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { equvivalent } from '../../../../../../util/Helper';
import { CoreDnpActivityListing, DnpSchoolType, CoreDnpActivityMappingView, CoreDnpExportActivityMapping, DnpActivity } from '../../../../../../models/CoreDnpModels'
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FlowSyncAccessPolicy, isAuthorized } from '../../../../../../models/FlowSyncAccessPolicy';
import { EmptyRequestState, RequestState, CacheRequestState } from '../../../../../../models/RequestState';
import { PageResult } from '../../../../../../models/CoreModels';
import ModalComponent from '../../../../../misc/ModalComponent';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../../../store/store';

const tabsValue = {
    MAPPED: 'mapped',
    UNMAPPED: 'unmapped',
    INCORRECT: 'incorrect',
};


const DnpActivitiesComponent = () => {
    const dispatch = useDispatch();
    const { dnpSchoolUnitId, dnpExportId } = useParams();
    
    const [searchParams, setSearchParams] = useSearchParams({ filterType: tabsValue.MAPPED });
    const [defaultSchoolUnitType, setDefaultSchoolUnitType] = useState<string | undefined>(undefined);
    const [schoolUnitId, setSchoolUnitIds] = useState<string[] | undefined>(undefined);

    const activitiesBySchool = useSelector<RootState, CacheRequestState<PageResult<CoreDnpActivityListing>>>(state => state.dnp.activitiesBySchool);
    const dnpActivities = useSelector<RootState, RequestState<DnpActivity[]>>(state => state.dnp.dnpActivities);
    const saveActivityMappings = useSelector<RootState, EmptyRequestState>(state => state.dnp.saveActivityMappings);
    const coreSchoolUnit = useSelector<RootState, RequestState<CoreDnpActivityMappingView>>(state => state.dnp.mappedActivites);
    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);
    const dnpSchoolTypes = useSelector<RootState, RequestState<DnpSchoolType[]>>(state => state.dnp.dnpSchoolTypes);
    const saveActivityMappingsState = useSelector<RootState, RequestState<SaveActivityMappingsState>>(state => state.dnp.saveActivityMappingsState);
    const { t } = useTranslation(['configuration', 'common']);
    
    const getDefaultSchoolType = () => {
        if (!defaultSchoolUnitType) {
            const skolverketSchoolTypes: string[] = [];
            if (coreSchoolUnit.code === StatusCode.COMPLETE) {
                const schoolTypeIds = coreSchoolUnit.data.schoolUnits.map(s => s.schoolTypes.id)

                schoolTypeIds.map(singleSchoolTypeIds => {
                    if ((singleSchoolTypeIds & 0x58) > 0) {
                        skolverketSchoolTypes.push("GR");
                    }
                    if ((singleSchoolTypeIds & 0x20) > 0) {
                        skolverketSchoolTypes.push("SP");
                    }
                    if ((singleSchoolTypeIds & 0x180) > 0) {
                        skolverketSchoolTypes.push("GY");
                    }
                    if ((singleSchoolTypeIds & 0x600) > 0) {
                        skolverketSchoolTypes.push("VUXGY");
                    }
                })
            }

            const distinctSkolverketSchoolTypes = [...new Set(skolverketSchoolTypes)]

            if (distinctSkolverketSchoolTypes.length > 1) {
                return undefined;
            }
            if (distinctSkolverketSchoolTypes.length === 1) {
                setDefaultSchoolUnitType(distinctSkolverketSchoolTypes[0]);
                updateFilter("schoolType", distinctSkolverketSchoolTypes[0]);
                return distinctSkolverketSchoolTypes[0];
            }

            return distinctSkolverketSchoolTypes[0];
        }
    }

    const disabled = !isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EDIT_INFORMATION);
    var filter = RequestParser.parseDnpActivitiesSearchPageRequest(searchParams);

    const [updatedMappedActivities, setUpdatedMappedActivities] = useState<CoreDnpExportActivityMapping[]>([]);
    const [hasUpdated, setHasUpdated] = useState(false);

    if (!hasUpdated && coreSchoolUnit.code === StatusCode.COMPLETE && !updatedMappedActivities.length) {
        setHasUpdated(true);
        setUpdatedMappedActivities(coreSchoolUnit.data.dnpActivityMappings);
    };

    useEffect(() => {
        if (dnpExportId && dnpSchoolUnitId) {
            GetMappedDnpActivities(dnpExportId, dnpSchoolUnitId)(dispatch);
        }

        return () => {
            ClearActivitiesBySchool()(dispatch);
        }
    }, [dispatch, dnpExportId, dnpSchoolUnitId]);

    useEffect(() => {
        if (coreSchoolUnit.code === StatusCode.COMPLETE) {
            GetDnpActivities(coreSchoolUnit.data.dnpEnvironment)(dispatch);
            GetDnpSchoolTypes(coreSchoolUnit.data.dnpEnvironment)(dispatch);
        }
    }, [dispatch, coreSchoolUnit.code === StatusCode.COMPLETE])

    useEffect(() => {
        if (coreSchoolUnit.code === StatusCode.COMPLETE && schoolUnitId === undefined) {
            var schoolUnitIds: string[] = coreSchoolUnit.data.schoolUnits.map(x => x.schoolUnitId)
            setSchoolUnitIds(schoolUnitIds);
            updateFilter('schoolUnitIds', schoolUnitIds)
        }
    }, [coreSchoolUnit.code === StatusCode.COMPLETE, schoolUnitId]);

    useEffect(() => {
        let forceUpdate = false;
        if (saveActivityMappings.code === StatusCode.COMPLETE) {
            forceUpdate = true;
            if (coreSchoolUnit.code === StatusCode.COMPLETE) {
                setUpdatedMappedActivities(coreSchoolUnit.data.dnpActivityMappings);
            }
            ResetSaveActivityMappings(1500)(dispatch);
        }

        if (coreSchoolUnit.code === StatusCode.COMPLETE && dnpSchoolUnitId && schoolUnitId !== undefined) {
            GetActivitiesBySchool(dnpSchoolUnitId, filter, forceUpdate)(dispatch);
        }
    }, [dispatch, searchParams, saveActivityMappings, coreSchoolUnit, schoolUnitId]);

    useEffect(() => {
        if (dnpExportId && dnpSchoolUnitId && saveActivityMappingsState.code === StatusCode.COMPLETE && saveActivityMappingsState.data.allMappingsAreValid) {
            SaveActivityMappings(dnpExportId, dnpSchoolUnitId, saveActivityMappingsState.data.updatedMappedActivities)(dispatch);
        }
    }, [dispatch, dnpExportId, dnpSchoolUnitId, saveActivityMappingsState])

    const updateTab = (name: string, value: string) => {
        ClearActivitiesBySchool()(dispatch);
        updateFilter(name, value);
    }

    const updateFilter = (name: string, value: string | null | undefined | number | number[] | string[]) => {
        if (name !== 'page') {
            searchParams.set('page', '1');
        }

        if (typeof (value) === 'object') {
            if (value !== null && value.length > 0) {
                searchParams.delete(name)

                value.forEach(x => {
                    searchParams.append(name, x.toString())
                })
            } else {
                searchParams.delete(name)
            }
        }
        else if (value !== undefined && value !== '' && value !== null) {
            searchParams.set(name, value.toString());
        } else {
            searchParams.delete(name);
        }

        setSearchParams(searchParams);
    };

    const clearFilter = () => {
        if (schoolUnitId !== undefined) {
            if (filter.filterType === undefined) {
                setSearchParams({ filterType: tabsValue.UNMAPPED, schoolUnitIds: schoolUnitId });
                return;
            }

            if ((filter.schoolType !== undefined) && filter.schoolType === defaultSchoolUnitType) {
                setSearchParams({ filterType: filter.filterType, schoolType: filter.schoolType, schoolUnitIds: schoolUnitId });
                return;
            }

            setSearchParams({ filterType: filter.filterType, schoolUnitIds: schoolUnitId });
        }
    };

    const beginSaveMappings = () => {
        if (dnpExportId && dnpSchoolUnitId && coreSchoolUnit.code === StatusCode.COMPLETE) {
            BeginSaveActivityMappings(coreSchoolUnit.data.dnpEnvironment, updatedMappedActivities)(dispatch);
        }
    }

    const abortSaveMappings = () => {
        AbortSaveActivityMappings()(dispatch);
    }

    const confirmSaveMappings = () => {
        if (dnpExportId && dnpSchoolUnitId && saveActivityMappingsState.code === StatusCode.COMPLETE) {
            SaveActivityMappings(dnpExportId, dnpSchoolUnitId, saveActivityMappingsState.data.updatedMappedActivities)(dispatch);
        }
    }

    const translateSchoolType = (schoolType: string) => {
        if (dnpSchoolTypes.code === StatusCode.COMPLETE) {
            const dnpSchoolType = dnpSchoolTypes.data.find(st => st['skv:identifier'] === schoolType)?.['skv:title'];
            if (dnpSchoolType !== undefined) {
                return dnpSchoolType;
            } else {
                return schoolType;
            }
        }
        return schoolType;
    }

    const renderActivitiesTable = (header: string[], mobileHeader: string, resultsMissing: string) => {
        return (<>
            <div className={(`columns-${header.length}`) + " dnp-activities grid-table"}>
                <div className="general-header --header">
                    <span>{t('dnpActivities.generalHeaderFlowsyncActivity', {ns: 'configuration'})}</span>
                    <span>{t('dnpActivities.generalHeaderExamActivity', {ns: 'configuration'})}</span>
                </div>
                <div className="--header">
                    {header.map((x, i) => (
                        <span key={i}>{x}</span>))}
                </div>
                <div className="sm-header">{mobileHeader}</div>
                {renderActivities(resultsMissing)}
            </div>
        </>);
    };

    const renderActivities = (resultsMissing: string) => {
        if (!dnpExportId || !dnpSchoolUnitId) {
            return; // The default 404 page handles when all required url parameters are not present.
        } else if (!activitiesBySchool || (activitiesBySchool.code === StatusCode.PENDING && !activitiesBySchool.data?.values.length)) {
            return <Loading />;
        } else if (activitiesBySchool.code === StatusCode.COMPLETE && !activitiesBySchool.data.values.length) {
            if (filter.search || filter.syllabusId) {
                return <div className="pt-2 ps-2">{t('dnpActivities.noMatchingResult', {ns: 'configuration'})}</div>
            } else {
                return <div className="pt-2 ps-2">{resultsMissing}</div>
            }
        } else if (activitiesBySchool.code === StatusCode.COMPLETE
            || (activitiesBySchool.code === StatusCode.PENDING && activitiesBySchool.data?.values && activitiesBySchool.data.values.length > 1)) {
            return (<>
                {activitiesBySchool.code === StatusCode.PENDING && <Loading className="spinner-update" />}
                {
                    filter.filterType === tabsValue.UNMAPPED ?
                        <UnmappedDnpActivities
                            dnpExportId={dnpExportId}
                            dnpSchoolUnitId={dnpSchoolUnitId}
                            activitiesBySchool={activitiesBySchool}
                            dnpActivities={dnpActivities}
                            filter={filter}
                            disabled={disabled}
                            updatedMappedActivities={updatedMappedActivities}
                            onUpdate={(updatedMappedActivities) => setUpdatedMappedActivities(updatedMappedActivities)}
                        /> :
                        <MappedDnpActivities
                            dnpExportId={dnpExportId}
                            dnpSchoolUnitId={dnpSchoolUnitId}
                            activitiesBySchool={activitiesBySchool}
                            dnpActivities={dnpActivities}
                            filter={filter}
                            disabled={disabled}
                            updatedMappedActivities={updatedMappedActivities}
                            onUpdate={(updatedMappedActivities) => setUpdatedMappedActivities(updatedMappedActivities)}
                            mappedActivites={coreSchoolUnit.code === StatusCode.COMPLETE ? coreSchoolUnit.data.dnpActivityMappings : []}
                            translateSchoolType={(schoolType) => translateSchoolType(schoolType)}
                        />
                }
            </>);
        } else if (activitiesBySchool.code === StatusCode.ERROR) {
            return <span>{t('dnpActivities.somethingWentWrong', {ns: 'configuration'})}</span>
        };
    };

    const confirmSaveWithInvalidMappingsModal = () => {
        if (saveActivityMappingsState.code !== StatusCode.COMPLETE) {
            return;
        }

        if (saveActivityMappingsState.data.allMappingsAreValid) {
            return;
        }

        return (
            <ModalComponent
                isOpen={saveActivityMappings.code === StatusCode.PENDING}
                toggleModal={abortSaveMappings}
                header={t('dnpActivities.incorrectlyConnected.header', {ns: 'configuration'})}
                size="sm"
                cancel={t('no', {ns: 'common'})}
                confirm={t('yes', {ns: 'common'})}
                update={confirmSaveMappings}
            >
                <p>{t('dnpActivities.incorrectlyConnected.remainingMessage', {ns: 'configuration'})}</p>
            </ModalComponent>
        )
    }

    const tabs = () => {
        const hasInvalid = coreSchoolUnit.code === StatusCode.COMPLETE && coreSchoolUnit.data.dnpActivityMappings.some(x => !x.valid);
        let incorrectTitle: ReactNode = t('dnpActivities.labels.incorrectTitle', {ns: 'configuration'});
        if (hasInvalid) {
            incorrectTitle = (<span>{t('dnpActivities.labels.incorrectTitle', {ns: 'configuration'})} <FontAwesomeIcon color={'orangered'} icon={'triangle-exclamation'} /></span>)
        }

        return (
            <div className="tabs">
                {tab(tabsValue.MAPPED, t('dnpActivities.labels.mapped', {ns: 'configuration'}), 'check', undefined)}
                {tab(tabsValue.UNMAPPED, t('dnpActivities.labels.unMapped', {ns: 'configuration'}), 'exclamation', undefined)}
                {tab(tabsValue.INCORRECT, incorrectTitle, 'triangle-exclamation', hasInvalid ? 'orangered' : undefined)}
            </div>);
    };

    const tab = (tabsValue: string, title: ReactNode, icon: IconProp, color: string | undefined) => (<>
        <button className={(filter.filterType === tabsValue ? "active " : '') + "tab lg"} onClick={() => updateTab('filterType', tabsValue)}>{title}</button>
        <button className={(filter.filterType === tabsValue ? "active " : '') + "tab sm"} onClick={() => updateTab('filterType', tabsValue)}><FontAwesomeIcon color={color} icon={icon} /></button>
    </>);

    const renderBody = () => {
        switch (filter.filterType) {
            case tabsValue.MAPPED:
                return renderActivitiesTable([t('genericTableHeaders.name', {ns: 'common'}), t('genericTableHeaders.school', {ns: 'common'}), t('dnpActivities.labels.grade', {ns: 'configuration'}),  t('genericTableHeaders.startDate', {ns: 'common'}), t('genericTableHeaders.endDate', {ns: 'common'}), t('dnpActivities.labels.courseSubject', {ns: 'configuration'}), t('dnpActivities.labels.examActivity', {ns: 'configuration'}), t('dnpActivities.labels.status', {ns: 'configuration'}), ''], t('dnpActivities.labels.mapped', {ns: 'configuration'}), t('dnpActivities.labels.messageNoConnectedActivities', {ns: 'configuration'}));
            case tabsValue.UNMAPPED:
                return renderActivitiesTable([t('genericTableHeaders.name', {ns: 'common'}), t('genericTableHeaders.school', {ns: 'common'}), t('dnpActivities.labels.grade', {ns: 'configuration'}), t('genericTableHeaders.startDate', {ns: 'common'}), t('genericTableHeaders.endDate', {ns: 'common'}), t('dnpActivities.labels.courseSubject', {ns: 'configuration'}), t('dnpActivities.labels.examActivity', {ns: 'configuration'})], t('dnpActivities.labels.messageNotConnectedActivities', {ns: 'configuration'}), t('dnpActivities.labels.messageMissingActivities', {ns: 'configuration'}));
            case tabsValue.INCORRECT:
                return renderActivitiesTable([t('genericTableHeaders.name', {ns: 'common'}), t('genericTableHeaders.school', {ns: 'common'}), t('dnpActivities.labels.grade', {ns: 'configuration'}), t('genericTableHeaders.startDate', {ns: 'common'}), t('genericTableHeaders.endDate', {ns: 'common'}), t('dnpActivities.labels.courseSubject', {ns: 'configuration'}), t('dnpActivities.labels.examActivity', {ns: 'configuration'}), t('dnpActivities.labels.status', {ns: 'configuration'}), ''], t('dnpActivities.labels.messageIncorrectlyConectedActivities', {ns: 'configuration'}), t('dnpActivities.labels.messageNoIncorrectlyConectedActivities', {ns: 'configuration'}));
            default: return;
        };
    };

    const header = coreSchoolUnit.code === StatusCode.COMPLETE && `: ${coreSchoolUnit.data.dnpSchoolUnitDisplayName} -
        ${coreSchoolUnit.data?.schoolUnits?.length > 0 && coreSchoolUnit.data?.schoolUnits.map(s => s.schoolUnitDisplayName).join(', ')}`;

    return (<>
        <div className="dnp-container">
            <h2 className="mb-4">{t('dnpActivities.dnpContainer.header', { ns: 'configuration' })}{header} <a href="https://flowsync.se/help/modules/export/dnp#aktivitetskoppling" target="_blank"><FontAwesomeIcon className="documentation-icon fs-color" title={t('showHelp', {ns: 'common'})} icon="question-circle" /></a></h2>

            {dnpActivities.code === StatusCode.ERROR &&
                <div style={{ border: '2px solid orangered', padding: '1rem', marginBottom: '1rem' }}>{t('dnpActivities.dnpContainer.statusCodeErrorMessage', {ns: 'configuration'})}</div>}

            {dnpActivities && coreSchoolUnit.code === StatusCode.COMPLETE &&
                <DnpActivitiesFilter
                    environment={coreSchoolUnit.data.dnpEnvironment}
                    dnpActivities={dnpActivities}
                    updateFilter={(name, value) => updateFilter(name, value)}
                    schoolUnitIds={schoolUnitId}
                    clearFilter={clearFilter}
                    filter={filter}
                    translateSchoolType={(schoolType) => translateSchoolType(schoolType)}
                    defaultSchoolType={getDefaultSchoolType()}
                    dnpSchoolUnitId={dnpSchoolUnitId}
                    schoolUnits={coreSchoolUnit.code === StatusCode.COMPLETE ? coreSchoolUnit.data.schoolUnits : []}
                />}
            {tabs()}
            {renderBody()}
            {confirmSaveWithInvalidMappingsModal()}
        </div>
        {activitiesBySchool &&
            <DnpActivitiesFooter
                saveConfiguration={beginSaveMappings}
                updateFilter={(name, value) => updateFilter(name, value)}
                saveStatus={saveActivityMappings.code}
                disabledSave={equvivalent(updatedMappedActivities, (coreSchoolUnit.code === StatusCode.COMPLETE ? coreSchoolUnit.data.dnpActivityMappings : []))}
                requestState={activitiesBySchool}
            />
        }
    </>);
}

export default DnpActivitiesComponent;
